import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const ReportPreviewArea = ({ reportData, onExport, onSchedule, onBookmark }) => {
  const COLORS = ['#2563EB', '#0EA5E9', '#059669', '#D97706', '#DC2626', '#8B5CF6'];

  const monthlyData = [
    { month: 'Jan', receita: 45000, despesa: 32000, lucro: 13000 },
    { month: 'Fev', receita: 52000, despesa: 35000, lucro: 17000 },
    { month: 'Mar', receita: 48000, despesa: 33000, lucro: 15000 },
    { month: 'Abr', receita: 61000, despesa: 38000, lucro: 23000 },
    { month: 'Mai', receita: 55000, despesa: 36000, lucro: 19000 },
    { month: 'Jun', receita: 67000, despesa: 41000, lucro: 26000 }
  ];

  const categoryData = [
    { name: 'Hospedagem', value: 35, amount: 14000 },
    { name: 'Transporte', value: 25, amount: 10000 },
    { name: 'Marketing', value: 15, amount: 6000 },
    { name: 'Salários', value: 20, amount: 8000 },
    { name: 'Outros', value: 5, amount: 2000 }
  ];

  const clientProfitability = [
    { client: 'Maria Santos', receita: 12500, lucro: 3750, margem: 30 },
    { client: 'João Silva', receita: 18000, lucro: 6300, margem: 35 },
    { client: 'Ana Costa', receita: 9500, lucro: 2375, margem: 25 },
    { client: 'Pedro Oliveira', receita: 15000, lucro: 4500, margem: 30 },
    { client: 'Carla Souza', receita: 11000, lucro: 3850, margem: 35 }
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })?.format(value);
  };

  if (!reportData) {
    return (
      <div className="h-full flex items-center justify-center bg-muted/30">
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Icon name="FileText" size={40} className="text-primary" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">Nenhum Relatório Gerado</h3>
          <p className="text-sm text-muted-foreground max-w-md">
            Configure os parâmetros no painel lateral e clique em "Gerar Relatório" para visualizar os dados
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-background">
      <div className="p-6 border-b border-border bg-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Resumo Mensal - Novembro 2025</h2>
            <p className="text-sm text-muted-foreground mt-1">Gerado em 28/11/2025 às 00:03</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              iconName="Bookmark"
              iconPosition="left"
              onClick={onBookmark}
            >
              Salvar
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="Calendar"
              iconPosition="left"
              onClick={onSchedule}
            >
              Agendar
            </Button>
            <Button
              variant="default"
              size="sm"
              iconName="Download"
              iconPosition="left"
              onClick={onExport}
            >
              Exportar
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="p-4 bg-primary/10 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="TrendingUp" size={20} className="text-primary" />
              <span className="text-sm font-medium text-foreground">Receita Total</span>
            </div>
            <p className="text-2xl font-bold text-foreground">R$ 67.000,00</p>
            <p className="text-xs text-muted-foreground mt-1">+12% vs mês anterior</p>
          </div>

          <div className="p-4 bg-error/10 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="TrendingDown" size={20} className="text-error" />
              <span className="text-sm font-medium text-foreground">Despesas Totais</span>
            </div>
            <p className="text-2xl font-bold text-foreground">R$ 41.000,00</p>
            <p className="text-xs text-muted-foreground mt-1">+8% vs mês anterior</p>
          </div>

          <div className="p-4 bg-success/10 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="DollarSign" size={20} className="text-success" />
              <span className="text-sm font-medium text-foreground">Lucro Líquido</span>
            </div>
            <p className="text-2xl font-bold text-foreground">R$ 26.000,00</p>
            <p className="text-xs text-muted-foreground mt-1">+18% vs mês anterior</p>
          </div>

          <div className="p-4 bg-accent/10 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Percent" size={20} className="text-accent" />
              <span className="text-sm font-medium text-foreground">Margem de Lucro</span>
            </div>
            <p className="text-2xl font-bold text-foreground">38,8%</p>
            <p className="text-xs text-muted-foreground mt-1">+2,1% vs mês anterior</p>
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <div className="bg-card rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Evolução Financeira Mensal</h3>
          <div className="w-full h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="month" stroke="var(--color-muted-foreground)" />
                <YAxis stroke="var(--color-muted-foreground)" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'var(--color-card)', 
                    border: '1px solid var(--color-border)',
                    borderRadius: '0.375rem'
                  }}
                  formatter={(value) => formatCurrency(value)}
                />
                <Legend />
                <Bar dataKey="receita" fill="#2563EB" name="Receita" />
                <Bar dataKey="despesa" fill="#DC2626" name="Despesa" />
                <Bar dataKey="lucro" fill="#059669" name="Lucro" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-card rounded-lg border border-border p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Distribuição de Despesas por Categoria</h3>
            <div className="w-full h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100)?.toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {categoryData?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'var(--color-card)', 
                      border: '1px solid var(--color-border)',
                      borderRadius: '0.375rem'
                    }}
                    formatter={(value, name, props) => [formatCurrency(props?.payload?.amount), name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-card rounded-lg border border-border p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Lucratividade por Cliente</h3>
            <div className="w-full h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={clientProfitability}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis dataKey="client" stroke="var(--color-muted-foreground)" angle={-45} textAnchor="end" height={100} />
                  <YAxis stroke="var(--color-muted-foreground)" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'var(--color-card)', 
                      border: '1px solid var(--color-border)',
                      borderRadius: '0.375rem'
                    }}
                    formatter={(value) => formatCurrency(value)}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="receita" stroke="#2563EB" name="Receita" strokeWidth={2} />
                  <Line type="monotone" dataKey="lucro" stroke="#059669" name="Lucro" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Detalhamento por Cliente</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Cliente</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-foreground">Receita</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-foreground">Lucro</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-foreground">Margem</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-foreground">Status</th>
                </tr>
              </thead>
              <tbody>
                {clientProfitability?.map((client, index) => (
                  <tr key={index} className="border-b border-border hover:bg-muted/50 transition-colors">
                    <td className="py-3 px-4 text-sm text-foreground">{client?.client}</td>
                    <td className="py-3 px-4 text-sm text-right text-foreground">{formatCurrency(client?.receita)}</td>
                    <td className="py-3 px-4 text-sm text-right text-success">{formatCurrency(client?.lucro)}</td>
                    <td className="py-3 px-4 text-sm text-right text-foreground">{client?.margem}%</td>
                    <td className="py-3 px-4 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        client?.margem >= 35 ? 'bg-success/10 text-success' :
                        client?.margem >= 30 ? 'bg-accent/10 text-accent': 'bg-warning/10 text-warning'
                      }`}>
                        <Icon name={client?.margem >= 35 ? 'TrendingUp' : client?.margem >= 30 ? 'Minus' : 'TrendingDown'} size={12} />
                        {client?.margem >= 35 ? 'Excelente' : client?.margem >= 30 ? 'Bom' : 'Regular'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Resumo Executivo</h3>
          <div className="space-y-4 text-sm text-muted-foreground">
            <p>
              <strong className="text-foreground">Desempenho Geral:</strong> O mês de novembro apresentou crescimento significativo de 12% na receita em comparação ao mês anterior, atingindo R$ 67.000,00. Este resultado foi impulsionado principalmente pelo aumento nas reservas de pacotes premium e pela expansão da base de clientes.
            </p>
            <p>
              <strong className="text-foreground">Controle de Despesas:</strong> As despesas totalizaram R$ 41.000,00, representando um aumento controlado de 8%. A maior parte dos gastos concentrou-se em hospedagem (35%) e transporte (25%), mantendo-se dentro dos parâmetros planejados.
            </p>
            <p>
              <strong className="text-foreground">Lucratividade:</strong> A margem de lucro líquido alcançou 38,8%, superando a meta estabelecida de 35%. Este resultado demonstra eficiência operacional e boa gestão de custos, com destaque para a otimização de despesas com marketing.
            </p>
            <p>
              <strong className="text-foreground">Análise de Clientes:</strong> João Silva e Carla Souza apresentaram as melhores margens de lucratividade (35%), enquanto Ana Costa requer atenção especial com margem de 25%. Recomenda-se revisão de estratégias de precificação para este segmento.
            </p>
            <p>
              <strong className="text-foreground">Recomendações:</strong> Manter foco em clientes de alta margem, investir em marketing digital para captação de novos clientes premium, e implementar programa de fidelidade para aumentar recorrência de reservas.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportPreviewArea;